<div class="subscription-upgrade-dialog">
  <div class="dialog-header">
    <h2 mat-dialog-title>{{ 'INTL_UPGRADE_SUBSCRIPTION' | translate }}</h2>
    <button
      mat-icon-button
      class="close-button"
      (click)="onCancel()"
      [disabled]="isLoading"
      aria-label="Close dialog"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content [class.contract-step]="currentStep === 'contract'">
    <div class="contract-block-banner" *ngIf="contractTemplateMissing && currentStep === 'tier'">
      <mat-icon>error_outline</mat-icon>
      <span>{{ 'INTL_ONBOARDING_CONTRACT_TEMPLATE_MISSING' | translate }}</span>
    </div>

    <wai-subscription-contract-signing
      *ngIf="currentStep === 'contract'"
      [embedSource]="contractSigningEmbed"
      [busy]="isContractBusy"
      [signingUnavailable]="contractSigningUnavailable"
      (continueClicked)="onContractSignedContinue()"
      (retryClicked)="onContractRetry()"
      (cancelClicked)="onContractCancelled()"
    ></wai-subscription-contract-signing>

    <!-- Tier Comparison + Selection (LP and GP, KAP-1849) -->
    <div *ngIf="(isLP || isGP) && currentStep !== 'contract'" class="lp-upgrade-content">
      <div class="billing-interval-selector" *ngIf="isLP && currentStep === 'tier'">
        <h3>{{ 'INTL_BILLING_INTERVAL' | translate }}</h3>
        <mat-radio-group [(ngModel)]="selectedBillingInterval">
          <mat-radio-button value="annual">{{ 'INTL_ANNUAL' | translate }}</mat-radio-button>
          <mat-radio-button value="monthly">{{ 'INTL_MONTHLY' | translate }}</mat-radio-button>
        </mat-radio-group>
      </div>

      <wai-sepa-payment-setup
        *ngIf="currentStep === 'sepa'"
        [billingName]="billingContactName"
        [billingEmail]="billingContactEmail"
        [processing]="isLoading"
        [trialPeriodDays]="sepaChargesNow ? 0 : getTrialPeriodDays(getSelectedTier$ | async)"
        (completed)="onSepaSetupCompleted($event)"
        (cancelled)="onSepaSetupCancelled()"
      ></wai-sepa-payment-setup>

      <ng-container *ngIf="currentStep === 'tier'">
      <div class="pending-change-banner" *ngIf="pendingPlanChange">
        <mat-icon>schedule</mat-icon>
        <div class="pending-content">
          <strong>{{ 'INTL_SCHEDULED_PLAN_CHANGE' | translate }} {{ pendingPlanChange.pendingTier.name }}</strong>
          <p>{{ 'INTL_EFFECTIVE_ON' | translate }} {{ pendingPlanChange.scheduledChangeAt | date }}</p>
        </div>
        <button mat-button color="warn" [disabled]="isLoading" (click)="onCancelScheduledDowngrade()">
          {{ 'INTL_CANCEL' | translate }}
        </button>
      </div>

      <ng-container *ngIf="getSelectedTier$ | async as selectedTier">
        <div *ngIf="getTrialPeriodDays(selectedTier) as trialDays" class="trial-highlight">
          <mat-icon>stars</mat-icon>
          <div class="trial-text">
            <strong>{{ (trialDays === 1 ? 'INTL_TRIAL_FREE_FOR_DAY' : 'INTL_TRIAL_FREE_FOR_DAYS') | translate : { days: trialDays } }}</strong>
            <p>{{ 'INTL_NO_PAYMENT_UNTIL_TRIAL_ENDS' | translate }}</p>
            <p class="trial-note">{{ 'INTL_TRIAL_BANK_DETAILS_BEFORE_TRIAL_ENDS' | translate }}</p>
          </div>
        </div>
      </ng-container>

      <div class="tier-selection">
        <h3>{{ 'INTL_SELECT_TIER' | translate }}</h3>
        <div class="tier-cards" *ngIf="availableTiers$ | async as tiers">
          <div *ngFor="let tier of getSortedTiers(tiers)" class="tier-card"
            [class.selected]="selectedTierUid === tier.uid"
            [class.tier-free]="tier.code === 'lp_free' || tier.code === 'gp_free'"
            [class.tier-standard]="tier.code === 'lp_standard' || tier.code === 'gp_basic'"
            [class.tier-professional]="tier.code === 'lp_professional' || tier.code === 'gp_professional'"
            [class.tier-enterprise]="tier.code === 'lp_enterprise' || tier.code === 'gp_enterprise'" (click)="onTierSelect(tier.uid)">
            <mat-icon class="tier-icon" [class.selected-icon]="selectedTierUid === tier.uid">{{ getTierIcon(tier.code)
              }}</mat-icon>
            <div class="tier-header">
              <h4>{{ tier.name }}</h4>
              <mat-icon *ngIf="selectedTierUid === tier.uid" class="check-icon">check_circle</mat-icon>
            </div>
            <div class="tier-price" *ngIf="hasTierDisplayPrice(tier)">
              <strong>{{ getTierDisplayPrice(tier) | currency : 'EUR' : 'symbol' : '1.0-2' }}</strong>
              <span>/ {{ tier.tierType === 'gp' || selectedBillingInterval === 'monthly' ? ('INTL_MONTHLY' | translate) : ('INTL_ANNUAL' | translate) }}</span>
            </div>
            <!-- Free-period badge on the tier itself, so a Free-plan user sees the trial
                 length without first selecting the tier (KAP-1854). Parameter-driven:
                 hidden when the tier has no configured trial. -->
            <div class="tier-trial-badge" *ngIf="getTrialPeriodDays(tier) as trialDays">
              <mat-icon>stars</mat-icon>
              <span>{{ (trialDays === 1 ? 'INTL_TRIAL_FREE_FOR_DAY' : 'INTL_TRIAL_FREE_FOR_DAYS') | translate : { days: trialDays } }}</span>
            </div>
            <div class="tier-description" *ngIf="tier.description">
              <ng-container *ngIf="getDescriptionList(tier.description) as descriptionItems">
                <ul class="tier-description-list" *ngIf="descriptionItems.length > 1">
                  <li *ngFor="let item of descriptionItems">
                    <mat-icon class="tick-icon">check</mat-icon>
                    <span>{{ item }}</span>
                  </li>
                </ul>
                <p *ngIf="descriptionItems.length === 1">{{ tier.description }}</p>
              </ng-container>
            </div>
            <div class="tier-actions" [class.visible]="selectedTierUid === tier.uid" *ngIf="!isPlanChangeConfirmPendingForTier(tier)">
              <button mat-raised-button [class]="getTierButtonClass(tier.code)"
                [class.selected-button]="selectedTierUid === tier.uid"
                [disabled]="isLoading || contractTemplateMissing || selectedTierUid !== tier.uid || (selectedTierUid === tier.uid && selectedTierUid === currentSubscription?.tier.uid && !canReactivateCurrentTier()) || !canUpgrade()"
                (click)="onUpgrade(); $event.stopPropagation()">
                <mat-spinner *ngIf="isLoading && selectedTierUid === tier.uid" diameter="16"
                  class="button-spinner"></mat-spinner>
                <span *ngIf="!(isLoading && selectedTierUid === tier.uid)">
                  {{ getButtonText(tier) | translate }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="plan-change-preview" #planChangePreviewBlock *ngIf="planChangePreview">
        <h3>{{ 'INTL_PLAN_CHANGE_PREVIEW' | translate }}</h3>
        <div class="preview-grid">
          <div class="preview-row">
            <span>{{ 'INTL_CURRENT_PLAN' | translate }}</span>
            <strong>{{ planChangePreview.currentTier.name }}</strong>
          </div>
          <div class="preview-row">
            <span>{{ 'INTL_TARGET_PLAN' | translate }}</span>
            <strong>{{ planChangePreview.targetTier.name }}</strong>
          </div>
          <div class="preview-row">
            <span>{{ 'INTL_CHANGE_TYPE' | translate }}</span>
            <strong>{{ (planChangePreview.changeType === 'downgrade' ? 'INTL_DOWNGRADE' : 'INTL_UPGRADE') | translate }}</strong>
          </div>
          <div class="preview-row" *ngIf="!planChangePreview.isTrial">
            <span>{{ 'INTL_OUTSTANDING_CREDIT' | translate }}</span>
            <strong>€{{ planChangePreview.outstandingCredit | number : '1.2-2' }}</strong>
          </div>
          <div class="preview-row highlight">
            <span>{{ 'INTL_AMOUNT_DUE_NOW' | translate }}</span>
            <strong>€{{ planChangePreview.upgradePayableNow | number : '1.2-2' }}</strong>
          </div>
          <div class="preview-row" *ngIf="!planChangePreview.startsFresh">
            <span>{{ (planChangePreview.isTrial ? 'INTL_DAYS_LEFT_IN_TRIAL' : 'INTL_DAYS_REMAINING_CURRENT_PERIOD') | translate }}</span>
            <strong>{{ planChangePreview.daysRemaining }}</strong>
          </div>
        </div>
        <p class="preview-note">
          {{ 'INTL_PLAN_CHANGE_PREVIEW_NOTE' | translate }}
        </p>
        <p class="preview-note" *ngIf="planChangePreview.isTrial && planChangePreview.trialEnd">
          {{ 'INTL_PLAN_CHANGE_TRIAL_NOTE' | translate : { date: (planChangePreview.trialEnd | date : 'longDate') } }}
        </p>
        <p class="preview-note" *ngIf="planChangePreview.startsFresh">
          {{ 'INTL_GP_UPGRADE_STARTS_FRESH_NOTE' | translate }}
        </p>
        <div class="preview-actions">
          <button mat-stroked-button type="button" [disabled]="isLoading" (click)="onDismissPlanChangePreview()">
            {{ 'INTL_CANCEL' | translate }}
          </button>
          <button mat-raised-button color="primary" type="button" [disabled]="isLoading" (click)="onConfirmPlanChange()">
            <mat-spinner *ngIf="isLoading" diameter="16" class="button-spinner"></mat-spinner>
            <span *ngIf="!isLoading">{{ 'INTL_CONFIRM' | translate }}</span>
          </button>
        </div>
      </div>

      <div class="comparison-table">
        <h3>{{ 'INTL_TIER_COMPARISON' | translate }}</h3>
        <table mat-table [dataSource]="comparisonRows" class="comparison-table-content">
          <ng-container matColumnDef="feature">
            <th mat-header-cell *matHeaderCellDef>{{ 'INTL_FEATURE' | translate }}</th>
            <td mat-cell *matCellDef="let row">{{ row.feature | translate }}</td>
          </ng-container>

          <ng-container matColumnDef="free">
            <th mat-header-cell *matHeaderCellDef>{{ 'INTL_FREE' | translate }}</th>
            <td mat-cell *matCellDef="let row">
              <span *ngIf="typeof row.free === 'string'">{{ row.free | translate }}</span>
              <mat-icon *ngIf="row.free === true" color="primary">check</mat-icon>
              <mat-icon *ngIf="row.free === false" color="warn">close</mat-icon>
            </td>
          </ng-container>

          <ng-container matColumnDef="standard">
            <th mat-header-cell *matHeaderCellDef>{{ (isGP ? 'INTL_BASIC' : 'INTL_STANDARD') | translate }}</th>
            <td mat-cell *matCellDef="let row">
              <span *ngIf="typeof row.standard === 'string'">{{ row.standard | translate }}</span>
              <mat-icon *ngIf="row.standard === true" color="primary">check</mat-icon>
              <mat-icon *ngIf="row.standard === false" color="warn">close</mat-icon>
            </td>
          </ng-container>

          <ng-container matColumnDef="professional">
            <th mat-header-cell *matHeaderCellDef>{{ 'INTL_PROFESSIONAL' | translate }}</th>
            <td mat-cell *matCellDef="let row">
              <span *ngIf="typeof row.professional === 'string'">{{ row.professional | translate }}</span>
              <mat-icon *ngIf="row.professional === true" color="primary">check</mat-icon>
              <mat-icon *ngIf="row.professional === false" color="warn">close</mat-icon>
            </td>
          </ng-container>

          <ng-container matColumnDef="enterprise">
            <th mat-header-cell *matHeaderCellDef>{{ 'INTL_ENTERPRISE' | translate }}</th>
            <td mat-cell *matCellDef="let row">
              <span *ngIf="typeof row.enterprise === 'string'">{{ row.enterprise | translate }}</span>
              <mat-icon *ngIf="row.enterprise === true" color="primary">check</mat-icon>
              <mat-icon *ngIf="row.enterprise === false" color="warn">close</mat-icon>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
      </div>

      <!-- GP add-on selector (Featured Fund / Placement Agent) - orthogonal to
           the tier ladder above, unaffected by KAP-1849. -->
      <div class="gp-addon-section" #gpAddonSection *ngIf="isGP">
        <wai-gp-addon-selector [currentSubscription]="currentSubscription" [showPricing]="true" [allowMultiple]="true"
          (subscriptionUpdated)="onSubscriptionUpdated($event)">
        </wai-gp-addon-selector>
      </div>
      </ng-container>
    </div>

  </mat-dialog-content>

  <!-- Proceed Button for GP users -->
  <mat-dialog-actions *ngIf="isGP && canSubscribe() && currentStep === 'tier'" align="end" class="gp-subscribe-actions">
    <button mat-raised-button color="primary" [disabled]="isLoading || contractTemplateMissing" (click)="onProceed()" class="subscribe-button">
      <mat-spinner *ngIf="isLoading" diameter="16" class="button-spinner"></mat-spinner>
      <span *ngIf="!isLoading">{{ 'INTL_CONTINUE' | translate }}</span>
    </button>
  </mat-dialog-actions>
</div>